import {useState, useCallback, useEffect, useRef, useMemo} from 'react'
import {serverDirectionFor, recordServerDirection} from '@/utils/serverDirection'
import {useNavigate} from 'react-router'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {tripService, isRouteHashError, isTripNotDraftError, confirmErrorMessage, isPaymentMethodUnusableError, PAYMENT_METHOD_UNUSABLE_MESSAGE} from '@/services/tripService'
import {isIdentityIncompleteError, identityMissingFromError} from '@/utils/identityGate'
import {useTripDetails} from '@/hooks/useTrips'
import {useTriDirection, isNearAirport} from '@/hooks/useAirportDetector'
import {getAirportIdsFor} from '@/utils/airports'
import {ensureAirports} from '@/hooks/useAirports'
import {BOOKING_CONSTRAINTS, TRIP_DIRECTIONS} from '@/constants/booking'
import {tripNow, formatTripLocalTime, roundToNext5Min, getMinLeadTimeStr} from '@/utils/dateUtils'
import {useAppConfig} from '@/hooks/useAppConfig'
import {tripCancelPolicy} from '@/utils/functions'
import {usePolicies} from '@/hooks/usePolicies'
import {usePassengerTypes, useChildSeatTypes, useLuggageTypes} from '@/hooks/useSelectables'
import {reverseGeocodePostalCode} from '@/customComponents/uiComponents/global/PlaceAutocomplete.jsx'

const EDIT_CACHE_PREFIX = 'hopn-edit-trip-'


const SECTION_FIELDS = {
    destination: ['origin', 'destination', 'stop_overs'],
    // Split parts of the destination editor (separate origin vs drop-off modals)
    // so each card gates on just its own field(s).
    dropoff: ['destination', 'stop_overs'],
    passengers: ['passenger_types', 'child_seats'],
    luggage: ['luggage'],
    car: ['vehicle_type_id'],
    special_request: ['special_request', 'having_pet', 'quiet_ride'],
}

function extractLocation(loc) {
    if (!loc) return {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}
    if (typeof loc === 'string') return {address: loc, lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}
    return {address: loc.address || '', lat: loc.lat || '', lng: loc.lng || '', city: loc.city || '', state: loc.state || '', country: loc.country || '', postal_code: loc.postal_code || ''}
}

// ── calculate-cost / update input builders (shared by the preview recalcs + submit) ──
const toPassengerArr = (map) => Object.entries(map).map(([id, qty]) => ({passenger_type_id: parseInt(id), quantity: qty})).filter(p => p.quantity > 0)
const toChildSeatArr = (map) => Object.entries(map).map(([id, qty]) => ({child_seat_type_id: parseInt(id), quantity: qty})).filter(s => s.quantity > 0)
const toLuggageArr = (std, over) => [
    ...Object.entries(std).map(([id, qty]) => ({luggage_type_id: parseInt(id), quantity: qty})),
    ...Object.entries(over).map(([id, qty]) => ({luggage_type_id: parseInt(id), quantity: qty})),
].filter(l => l.quantity > 0)
// postal_code is REQUIRED by the backend on every location (same rule calculateRoute
// already follows in tripService). It was missing here, so the /trips/{id} update sent
// origin/destination without one — "The origin postal code is required." The value is
// carried through the whole edit state already (normalizeLocation keeps it off the trip,
// DestinationSection writes it from PlaceAutocomplete's reverse-geocode fallback); only
// this payload builder dropped it.
const toLocationPayload = (l) => ({lat: parseFloat(l.lat), lng: parseFloat(l.lng), address: l.address, city: l.city || '', state: l.state || '', country: l.country || '', postal_code: l.postal_code || ''})

// trip_direction from a pair of live locations (matches useTriDirection semantics).
const directionFor = (orig, dest) => {
    // The server's direction (admin switch on) wins; otherwise our own coordinate match.
    const server = serverDirectionFor(orig, dest)
    if (server) return server.direction
    const from = Boolean(isNearAirport(orig?.lat, orig?.lng))
    const to = Boolean(isNearAirport(dest?.lat, dest?.lng)) && !from
    return from ? TRIP_DIRECTIONS.FROM_AIRPORT : to ? TRIP_DIRECTIONS.TO_AIRPORT : TRIP_DIRECTIONS.OTHERS
}

// The free-modification allowance is exhausted (used ≥ total, total > 0). While true the
// WHOLE edit form is read-only — every field/section is disabled even if the backend still
// lists it in editable_fields. Matches the header + submit-button "at limit" check.
const isAtModificationLimit = (policy) => {
    if (!policy) return false
    const used = Number(policy.modifications_used) || 0
    const total = Number(policy.free_modifications) || 0
    return total > 0 && used >= total
}
const NO_MODIFICATION_REASON = 'There is no modification available now'

/**
 * The /confirm page's data hook — a Draft becoming a live booking.
 *
 * A STANDALONE copy of useEditTrip, not a wrapper around it: confirming a Draft and editing a
 * live trip only look alike. A Draft response carries no `selected_vehicle`, no
 * `selectables.vehicle_types`, no `edit_policy`, no `cancellation_policy`, no price and no
 * route — so this page is treated exactly like creating a NEW booking, and the two flows are
 * kept in separate files so neither constrains the other.
 *
 * What differs from the edit hook:
 *  1. NO server-side fare, ever — price and vehicle list come only from our own
 *     calculate-route → calculate-cost pass, run on every load and again whenever the
 *     date/time changes (surge is time-windowed, so the re-quote is what reveals it).
 *  2. Route + locations come from the /calculate-route RESPONSE, measured on every load.
 *  3. Policies come from /trips/calculate-policies, not from the trip.
 *  4. Nothing to note here any more — the special request used to be the one section that
 *     did NOT prefill from the trip; it now does, like the rest. See the hydration.
 *  5. Submit is /confirm only; there is no doUpdate here.
 */
export default function useConfirmTrip(rideId) {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    // Live-poll the server trip so status/driver/payment changes show without a reload.
    // The user's un-submitted edits are NOT in this cache — they live in `previewOverlay`
    // (local state) below and are merged in at read, so the poll can't clobber them.
    const {data, isLoading: isTripLoading} = useTripDetails(rideId, {live: true})
    // Gate on /config/app the same way the booking modal does: subscribe so this
    // hook re-renders (and the min-lead-time validation recomputes) once the config
    // lands, and so the page holds the loading state until BOOKING_CONSTRAINTS can
    // read the real values from /config/app instead of the hardcoded fallbacks.
    const {data: appConfig, isLoading: isConfigLoading} = useAppConfig()

    // The un-submitted edit preview (recalculated price + edited fields), kept in memory
    // only — reload / leaving the page drops it, exactly like the rest of the edit state.
    // `applyPreview` fills it; the main submit and discard clear it.
    const [previewOverlay, setPreviewOverlay] = useState(null)
    // Reset if the trip id changes without a remount (defensive; routes normally remount).
    useEffect(() => { setPreviewOverlay(null) }, [rideId])

    // Displayed trip = live server copy with the edit overlay laid on top. The overlay
    // wins only for the fields the user edited; everything else (status, driver, …) stays
    // live from the poll — so a server-side cancellation shows mid-edit without losing edits.
    const trip = useMemo(() => {
        const server = data?.trip
        if (!server || !previewOverlay) return server
        return {
            ...server,
            ...previewOverlay.fields,
            selectables: previewOverlay.vehicleTypes
                ? {...server.selectables, vehicle_types: previewOverlay.vehicleTypes}
                : server.selectables,
        }
    }, [data?.trip, previewOverlay])

    const isLoading = isTripLoading || isConfigLoading

    const getSectionPolicy = useCallback((section) => {
        const policy = trip?.edit_policy
        if (!policy) return {canEdit: true, blockedReason: null}
        // Out of free modifications → nothing is editable, regardless of editable_fields.
        if (isAtModificationLimit(policy)) return {canEdit: false, blockedReason: NO_MODIFICATION_REASON}
        const fields = SECTION_FIELDS[section] || [section]
        const editable = policy.editable_fields || []
        const locked = policy.locked_reasons || {}
        // destination + passengers open if any of their sub-fields is editable
        // (passengers additionally overlays the locked sub-section inside its popup)
        if (section === 'destination' || section === 'dropoff' || section === 'passengers' || section === 'special_request') {
            if (fields.some(f => editable.includes(f))) return {canEdit: true, blockedReason: null}
            return {canEdit: false, blockedReason: locked[fields[0]] || policy.blocked_reason || null}
        }
        const firstBlocked = fields.find(f => !editable.includes(f))
        if (!firstBlocked) return {canEdit: true, blockedReason: null}
        return {canEdit: false, blockedReason: locked[firstBlocked] || policy.blocked_reason || null}
    }, [trip])

    const getFieldPolicy = useCallback((field) => {
        const policy = trip?.edit_policy
        if (!policy) return {canEdit: true, blockedReason: null}
        // Out of free modifications → nothing is editable, regardless of editable_fields.
        if (isAtModificationLimit(policy)) return {canEdit: false, blockedReason: NO_MODIFICATION_REASON}
        const editable = policy.editable_fields || []
        const locked = policy.locked_reasons || {}
        if (!editable.includes(field)) {
            return {canEdit: false, blockedReason: locked[field] || policy.blocked_reason || null}
        }
        return {canEdit: true, blockedReason: null}
    }, [trip])

    // Delegates to the shared gate — see useEditTrip.
    const getCancelPolicy = useCallback(() => tripCancelPolicy(trip), [trip])

    // ── Live state ──
    const [destinations, setDestinations] = useState({origin: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}, destination: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}, stopOvers: []})
    const [passengers, setPassengers] = useState({})
    const [childSeats, setChildSeats] = useState({})
    const [luggage, setLuggage] = useState({})
    const [oversizedLuggage, setOversizedLuggage] = useState({})

    // ── Committed state ──
    const [committedDestinations, setCommittedDestinations] = useState({origin: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}, destination: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}, stopOvers: []})
    const [committedPassengers, setCommittedPassengers] = useState({})
    const [committedChildSeats, setCommittedChildSeats] = useState({})
    const [committedLuggage, setCommittedLuggage] = useState({})
    const [committedOversizedLuggage, setCommittedOversizedLuggage] = useState({})

    // Vehicle
    const [selectedCar, setSelectedCar] = useState(null)
    const [committedCarTypeId, setCommittedCarTypeId] = useState(null)
    const [availableCars, setAvailableCars] = useState([])

    // Other
    const [selectedCardId, setSelectedCardId] = useState(null)
    const [savedCards, setSavedCards] = useState([])
    const [specialRequest, setSpecialRequest] = useState({notes: '', caringPet: false, quietRide: false})
    const [contact, setContact] = useState({full_name: '', email: '', phone: ''})
    // Empty = "not known yet", NOT 0%. A live trip fills it from
    // selected_vehicle.gratuity_percentage on hydration; a Draft has no selected_vehicle, so it
    // stays empty until the first calculate-cost response reports the gratuity the backend
    // applied. Do NOT seed it from DRIVER_TIP_OPTIONS[0]: that getter is empty until
    // /config/app lands, so at mount it yielded String(undefined) — which then went out on
    // calculate-cost as a NaN gratuity and priced the trip untipped.
    const [driverTip, setDriverTip] = useState('')
    // Same reasoning as driverTip above — and this one is what closeEdit restores, so seeding it
    // from the empty-at-mount DRIVER_TIP_OPTIONS getter put the literal "undefined" back into
    // the gratuity dropdown every time a section modal was closed.
    const [committedDriverTip, setCommittedDriverTip] = useState('')
    const [flightNumber, setFlightNumber] = useState('')
    // Applied coupon — kept so section/tip recalcs preserve the discount, and so the
    // main submit persists it. Seeded from the trip; updated by the coupon form.
    const [couponCode, setCouponCode] = useState('')

    // Selectables — the trip detail normally carries them (`?include_selectables=true`),
    // so these hold what the TRIP sent.
    const [tripPassengerTypes, setPassengerTypes] = useState([])
    const [tripChildSeatTypes, setChildSeatTypes] = useState([])
    const [tripLuggageTypes, setLuggageTypes] = useState([])

    // …but not every trip comes back with them, and App.jsx deliberately skips the
    // app-level selectables prefetch on /rides* — so this page had no fallback and the
    // lists could stay empty. That doesn't just blank the section pickers: applyPreview
    // rebuilds `selected_*` from these types, so an empty list rewrites the summary as
    // "No passengers selected" over a trip that has them. Fetch them here (same cached
    // queries the booking flow uses — free when already loaded) and fall back to them.
    const {data: apiPassengerTypes} = usePassengerTypes()
    const {data: apiChildSeatTypes} = useChildSeatTypes()
    const {data: apiLuggageTypes} = useLuggageTypes()

    // The trip's own lists win — they are the ones priced against this booking.
    const passengerTypes = tripPassengerTypes.length ? tripPassengerTypes : (apiPassengerTypes || [])
    const childSeatTypes = tripChildSeatTypes.length ? tripChildSeatTypes : (apiChildSeatTypes || [])
    const luggageTypes = tripLuggageTypes.length ? tripLuggageTypes : (apiLuggageTypes || [])

    // UI
    const [editingSection, setEditingSection] = useState(null)
    const [passengerView, setPassengerView] = useState('main')
    const [passengerOnly, setPassengerOnly] = useState(null) // 'passengers' | 'childseats' | null
    const [destinationPart, setDestinationPart] = useState(null) // 'origin' | 'dropoff' | null
    const [luggageOversizedMode, setLuggageOversizedMode] = useState(false)
    const [carBreakdownId, setCarBreakdownId] = useState(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isRecalculating, setIsRecalculating] = useState(false)
    // A user-chosen pickup is being re-quoted AND written to the trip. DateTimeCard locks
    // its date + time fields and shows the global loader in their place while this is true.
    const [isSavingDateTime, setIsSavingDateTime] = useState(false)
    const [sectionSaveError, setSectionSaveError] = useState(null)
    const [bookingError, setBookingError] = useState(null)
    // The identity fields the gate refused this confirm for, or null. Separate from
    // `bookingError` because it renders as a notice WITH a way out, not as error text.
    const [identityMissing, setIdentityMissing] = useState(null)
    const [showSuccess, setShowSuccess] = useState(false)
    const [expanded, setExpanded] = useState(new Set())
    const [confirmStep, setConfirmStep] = useState(0)
    const [showCancelPopup, setShowCancelPopup] = useState(false)
    const [showSRPopup, setShowSRPopup] = useState(false)
    const [infoPopup, setInfoPopup] = useState(null)
    const [showFlightPrompt, setShowFlightPrompt] = useState(false)
    const [flightFinalizeError, setFlightFinalizeError] = useState(null)

    // DateTime
    const [liveDatetime, setLiveDatetime] = useState({date: null, time: null, hour: null, minute: null})
    // The min-lead-time message is a call-to-action, not an automatic state: it must fire only after
    // the user actively (re)picks a date/time — never on first reload off the trip's existing
    // scheduled_at (which DateTimeCard commits into liveDatetime on mount). Set true by the user's
    // date/time pick handlers; the mount-commit and smart-time apply leave it false.
    const [dateTimeTouched, setDateTimeTouched] = useState(false)

    // Minimum booking lead-time validation (from /config/app booking.start_time_buffer).
    // The selected pickup must be at least MINIMUM_BOOKING_LEAD_TIME_MINUTES ahead of "now"
    // (tripNow() = APP_TIMEZONE wall-clock). Both Dates live in the same wall-clock frame, so
    // their epoch difference is the true wall-clock gap. Returns a warning string or null.
    const minLeadTimeError = useMemo(() => {
        // Runtime CTA: silent until the user actively picks a date/time (dateTimeTouched). Mirrors the
        // booking-flow wheel step (travelingDateTime.jsx `validateLeadTime`) EXACTLY: the picked time must
        // be at least the backend min lead time PLUS a 10-min FRONTEND buffer ahead of now (tripNow() =
        // APP_TIMEZONE wall-clock, same frame as the picker). The message shows the backend lead time;
        // "earliest available" is that buffered minAllowed rounded up to the next 5-min slot.
        if (!dateTimeTouched) return null
        if (!liveDatetime?.time) return null
        const selected = new Date(liveDatetime.time)
        if (isNaN(selected.getTime())) return null
        const minAllowed = new Date(tripNow().getTime() + (BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES + 10) * 60000)
        if (selected.getTime() >= minAllowed.getTime()) return null
        const earliest = roundToNext5Min(minAllowed)
        return `For quick bookings, please contact ${BOOKING_CONSTRAINTS.CONTACT_NUMBER}. Online booking is only available ${getMinLeadTimeStr()} in advance (earliest available: ${formatTripLocalTime(earliest)}).`
    }, [liveDatetime, appConfig, dateTimeTouched])

    // Shared flight state
    const [sharedFlightNumber, setSharedFlightNumber] = useState('')
    const [sharedFlightDetails, setSharedFlightDetails] = useState(null)
    const [sharedFlightLoading, setSharedFlightLoading] = useState(false)
    const [sharedFlightError, setSharedFlightError] = useState('')
    const [sharedTimeApplied, setSharedTimeApplied] = useState(false)
    // True once a flight lookup fails because the PROVIDER is unreachable (blocked key,
    // rate limit, 5xx, network) — NOT a clean "no such flight". Set by DateTimeSection's
    // lookups; stays true (across date/direction clears) until a lookup SUCCEEDS, so a
    // known outage keeps trusting the booking's saved flight instead of deadlocking.
    const [sharedFlightUnavailable, setSharedFlightUnavailable] = useState(false)

    // Flight-required gate value shared by the /edit + /confirm submit buttons. The gate is
    // satisfied only once the flight has been VALIDATED via the smart-suggestion lookup
    // (sharedFlightDetails is set only on a successful flight-API response) — typing a number
    // alone is not enough, so "Please enter your flight number" persists until the API returns
    // a matching flight. Exception: the trip's OWN saved flight, left untouched, was already
    // validated at booking, so it counts until the user changes it (or a direction flip clears it).
    // OUTAGE ESCAPE HATCH: if the provider is unreachable, we CAN'T re-validate — so a booking
    // that already has a saved flight is trusted rather than trapping the user behind a gate
    // that can never clear (see project note "flight gate deadlock on provider outage").
    // The passenger touched the flight field (typed, or ✕). From then on only a real lookup
    // (Add → sharedFlightDetails) shows the ✕ — retyping the trip's saved number keeps "Add".
    const [flightEdited, setFlightEdited] = useState(false)
    const hasFlightNumber = !!sharedFlightDetails
        || (!flightEdited && !!trip?.flight_number && sharedFlightNumber === trip.flight_number)
        // OUTAGE ESCAPE HATCH: sharedFlightUnavailable is set ONLY when the user clicks
        // "Proceed anyway" on the provider-unavailable card, so any flight number they
        // have entered is acknowledged — open the gate. Keyed off sharedFlightNumber (not
        // trip.flight_number) so it also works for a typed number / a trip with no saved flight.
        || (sharedFlightUnavailable && !!sharedFlightNumber)

    // Editing the field invalidates the flight COMPLETELY — the number on screen is no longer
    // the one the API confirmed, so every derived value goes with it. flightNumber (the
    // committed value the confirm payload reads) is cleared too: without that, a half-typed
    // edit would leave the OLD flight in the submit payload while the field showed something
    // else. Dropping the outage acknowledgement matters as well — it was granted for a
    // specific number, not for whatever replaces it.
    const handleFlightNumberChange = useCallback((v) => {
        setFlightEdited(true)
        setSharedFlightNumber(v)
        setSharedFlightDetails(null)
        setSharedFlightError('')
        setSharedFlightUnavailable(false)
        setSharedTimeApplied(false)
        setFlightNumber('')
    }, [])

    // The ✕ on the flight pill is `clearFlight`, defined with the flight write-through
    // further down — it needs persistTripFields, and a useCallback dep is read at render.

    // Hints
    const [dateHint, setDateHint] = useState(true)
    const [flightHint, setFlightHint] = useState(false)
    const [smartTimePopup, setSmartTimePopup] = useState(false)
    const skipHintTransition = useRef(false)
    const dateTimeSectionRef = useRef(null)
    const applySmartTimeRef = useRef(null)
    const prevAirportRef = useRef(null)
    const populatedRef = useRef(false)
    // Set by the Smart Time modal (DateTimeSection) to true while the looked-up flight is an
    // airport MISMATCH the user hasn't acknowledged ("Proceed anyway" not clicked). Read on
    // modal close to decide whether the unconfirmed flight should be dropped.
    const flightDropRef = useRef(false)

    // Airport detection
    const {isFromAirport, isToAirport} = useTriDirection(
        committedDestinations.origin.lat, committedDestinations.origin.lng,
        committedDestinations.destination.lat, committedDestinations.destination.lng
    )
    const tripDirection = isFromAirport ? TRIP_DIRECTIONS.FROM_AIRPORT : isToAirport ? TRIP_DIRECTIONS.TO_AIRPORT : TRIP_DIRECTIONS.OTHERS
    const isAirportTrip = isFromAirport || isToAirport

    // Hint effects
    useEffect(() => {
        if (prevAirportRef.current === isFromAirport) return
        prevAirportRef.current = isFromAirport
        setDateHint(true)
        setFlightHint(false)
    }, [isFromAirport])

    useEffect(() => {
        if (isFromAirport && !sharedFlightDetails && !flightHint) {
            setFlightHint(true)
            setDateHint(false)
        }
    }, [sharedFlightDetails])

    const handleDateChange = useCallback(() => {
        setDateTimeTouched(true)
        setSharedFlightNumber('')
        setSharedFlightDetails(null)
        setSharedFlightError('')
        setSharedTimeApplied(false)
        setDateHint(false)
        if (isFromAirport && !skipHintTransition.current) setFlightHint(true)
    }, [isFromAirport])

    // User actively (re)picked a time — arms the min-lead-time CTA (fires even if the same time is reselected).
    const handleTimeChange = useCallback(() => {
        setDateTimeTouched(true)
        setSharedTimeApplied(false)
    }, [])

    const handleSmartTimeSelect = useCallback((recommendedTime) => {
        if (applySmartTimeRef.current) {
            skipHintTransition.current = true
            applySmartTimeRef.current(recommendedTime)
            setTimeout(() => { skipHintTransition.current = false }, 0)
        }
        setSmartTimePopup(false)
        setDateHint(false)
        setFlightHint(false)
    }, [isFromAirport])

    // Close the Smart Time modal. If it's being dismissed on an unacknowledged airport mismatch
    // (user never clicked "Proceed anyway"), the flight was never confirmed — drop it so it
    // leaves the input and the flight-required gate re-applies.
    const closeSmartTime = useCallback(() => {
        if (flightDropRef.current) {
            setSharedFlightNumber('')
            setSharedFlightDetails(null)
            setSharedFlightError('')
            setSharedTimeApplied(false)
        }
        flightDropRef.current = false
        setSmartTimePopup(false)
    }, [])
    // DateTimeSection reports here whether closing now should drop the flight (unacknowledged mismatch).
    const setFlightDropOnClose = useCallback((v) => { flightDropRef.current = v }, [])

    // ── Populate ──
    useEffect(() => {
        if (!trip || populatedRef.current) return
        populatedRef.current = true
        // A trip the server typed from the airport zones already carries its direction; seed it
        // so the first paint follows the server, not our coordinate guess.
        if (trip.trip_direction_source === 'zone' && trip.trip_direction) {
            recordServerDirection(extractLocation(trip.origin), extractLocation(trip.destination), {trip_direction: trip.trip_direction, airport: trip.airport || null})
        }

        const orig = extractLocation(trip.origin)
        const dest = extractLocation(trip.destination)
        const stops = (trip.stop_overs || []).map((s, i) => ({id: Date.now() + i, ...extractLocation(s.location || s)}))
        const destData = {origin: orig, destination: dest, stopOvers: stops}
        setDestinations(destData)
        setCommittedDestinations(destData)

        const passMap = {}
        ;(trip.selected_passengers || []).forEach(p => { if (p.passenger_type_id) passMap[p.passenger_type_id] = parseInt(p.quantity) || 0 })
        setPassengers(passMap)
        setCommittedPassengers(passMap)

        const seatMap = {}
        ;(trip.selected_child_seats || []).forEach(s => { if (s.child_seat_type_id) seatMap[s.child_seat_type_id] = parseInt(s.quantity) || 0 })
        setChildSeats(seatMap)
        setCommittedChildSeats(seatMap)

        const lugMap = {}, overMap = {}
        ;(trip.selected_luggage || []).forEach(l => { const id = l.luggage_type_id, qty = parseInt(l.quantity) || 0; if (l.category === 'Oversized') overMap[id] = qty; else lugMap[id] = qty })
        setLuggage(lugMap); setCommittedLuggage(lugMap)
        setOversizedLuggage(overMap); setCommittedOversizedLuggage(overMap)

        // A Draft carries NO `selected_vehicle` — that object is the PRICED vehicle, and a
        // Draft has no price. The chosen vehicle TYPE still comes through on the trip, so
        // never treat a missing selected_vehicle as "no car picked": that would leave
        // vehicle_type_id out of the /confirm payload (422) and stop applyPreview from
        // matching a priced vehicle after calculate-cost. Resolve the id here; the effect
        // below turns it into a full car object once `selectables` has landed.
        const sv = trip.selected_vehicle
        const vtId = sv
            ? (sv.vehicle_type_id ?? sv.id)
            : (trip.vehicle_type_id ?? trip.vehicle_type?.id ?? trip.vehicle?.vehicle_type_id ?? null)
        if (sv) setSelectedCar(sv)
        if (vtId != null) setCommittedCarTypeId(vtId)

        const sel = trip.selectables
        if (sel) {
            if (sel.passenger_types) setPassengerTypes(sel.passenger_types)
            if (sel.child_seat_types) setChildSeatTypes(sel.child_seat_types)
            if (sel.luggage_types) setLuggageTypes(sel.luggage_types)
            const vtArr = (sel.vehicle_types || []).map(v => ({...v, vehicle_type_id: v.vehicle_type_id ?? v.id, vehicle_name: v.vehicle_name ?? v.name, vehicle_image: v.vehicle_image ?? v.image_url}))
            if (vtArr.length > 0) setAvailableCars(vtArr)
        }

        const payMethods = trip.passenger?.payment_methods || []
        setSavedCards(payMethods)
        /*
         * ONLY what the trip itself points at — the expanded object, else the raw id.
         *
         * It used to fall back to `payMethods[0]` when the trip named none, which made the
         * first saved card render as chosen on a draft that had no payment method at all.
         * That was already misleading; once the card selection became a write-through
         * (see handleCardSelect) it was a straight contradiction — the passenger saw a card
         * ticked while the desk's copy of the booking showed none, because nothing had been
         * selected and so nothing had been saved. Now a card is ticked only once someone
         * actually picks it, and picking it is what puts it on the trip.
         */
        setSelectedCardId(trip.selected_payment_method?.id ?? trip.payment_method_id ?? null)
        /*
         * PREFILLED from the trip, like every other section on the page.
         *
         * It used to start blank on the argument that a Draft is "a new booking", so the
         * card should show exactly what /confirm would send. That reasoning inverted once
         * drafts started arriving with a real special request on them: the phone desk
         * enters one on the caller's behalf, and a return draft inherits the parent's. The
         * passenger saw an empty card — and because `doConfirm` sends
         * `special_request: specialRequest.notes || ''`, confirming without touching it
         * WIPED what the agent had captured.
         *
         * `special_request` first, `trip_notes` as the fallback: the update/confirm payloads
         * send the former, older trips carry the latter, and the desk's own Booking Details
         * page reads the same pair.
         */
        setSpecialRequest({
            notes: trip.special_request || trip.trip_notes || '',
            caringPet: !!trip.having_pet,
            quietRide: !!trip.quiet_ride,
        })
        setContact({full_name: trip.passenger?.full_name || '', email: trip.passenger?.email || '', phone: trip.passenger?.phone || ''})
        // A LIVE trip carries its gratuity on the priced vehicle (same place RideDetails /
        // CompletedRideDetails read it). A DRAFT has no priced vehicle — but it DOES carry
        // `gratuity_percentage` at the top level of the trip, so read that before falling
        // back to the calculate-cost response (adopted in applyPreview). Without the third
        // read a draft saved with 25% opened showing the backend's default instead.
        // Only a real (> 0) value; see the note there.
        const tipPct = trip.selected_vehicle?.pricing?.gratuity_percentage
            ?? trip.selected_vehicle?.gratuity_percentage
            ?? trip.gratuity_percentage
        if (tipPct) { setDriverTip(String(tipPct)); setCommittedDriverTip(String(tipPct)) }
        setFlightNumber(trip.flight_number || '')
        if (trip.flight_number) setSharedFlightNumber(trip.flight_number)
        setCouponCode(trip.discount_code || trip.coupon_code || '')

        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith(EDIT_CACHE_PREFIX) && !key.startsWith(`${EDIT_CACHE_PREFIX}${rideId}-`)) sessionStorage.removeItem(key)
        })
    }, [trip, rideId])

    // ── Modal handlers ──
    const openEdit = useCallback((s, {passengerOnly = null, oversized = false, part = null} = {}) => { setSectionSaveError(null); setPassengerView('main'); setPassengerOnly(passengerOnly); setLuggageOversizedMode(oversized); setDestinationPart(part); setCarBreakdownId(null); setEditingSection(s) }, [])

    // Closing a section modal DISCARDS the in-progress edit: revert live state to the committed
    // baseline. Only Done saves. (No unsaved-changes prompt / "Discard Changes" button.)
    const closeEdit = useCallback(() => {
        setDestinations({...committedDestinations}); setPassengers({...committedPassengers}); setChildSeats({...committedChildSeats})
        setLuggage({...committedLuggage}); setOversizedLuggage({...committedOversizedLuggage})
        const cc = availableCars.find(c => String(c.vehicle_type_id ?? c.id) === String(committedCarTypeId))
        if (cc) setSelectedCar(cc)
        setDriverTip(committedDriverTip); setSectionSaveError(null); setCarBreakdownId(null); setEditingSection(null)
    }, [committedDestinations, committedPassengers, committedChildSeats, committedLuggage, committedOversizedLuggage, availableCars, committedCarTypeId, committedDriverTip])

    // ── Local price preview (NO persist) ──
    // DONE / tip / coupon no longer save. They re-price the CURRENT selection via
    // calculate-route → calculate-cost and write an OPTIMISTIC trip into the cache, so the
    // cards + breakdown update exactly as before. The real save happens only on the main
    // submit. A reload (without submitting) refetches the server trip and reverts — a true preview.
    const applyPreview = useCallback((vehicleTypes, dir) => {
        const mapped = (vehicleTypes || []).map(v => ({...v, vehicle_type_id: v.vehicle_type_id ?? v.id, vehicle_name: v.vehicle_name ?? v.name, vehicle_image: v.vehicle_image ?? v.image_url}))
        if (mapped.length) setAvailableCars(mapped)
        const selId = selectedCar?.vehicle_type_id ?? selectedCar?.id
        // Fall back to the FIRST option the quote returned when nothing is chosen yet.
        // A Draft arrives with no vehicle, and a previously-picked type can drop out of
        // the list when passengers/luggage change — either way the summary showed
        // "No vehicle selected" and made the user open Select Vehicle and press Done
        // before anything could be priced or confirmed.
        const selVehicle = mapped.find(v => String(v.vehicle_type_id) === String(selId)) || mapped[0] || null
        if (selVehicle) { setSelectedCar(selVehicle); setCommittedCarTypeId(selVehicle.vehicle_type_id) }
        // The gratuity % arrives HERE, on the calculate-cost response — not in the trip. On a
        // Draft the first quote is sent with no gratuity, so this is how the dropdown learns the
        // percentage the backend applied (and thereafter what gets sent back). Adopted straight
        // from the response rather than waiting for the trip-hydration effect to notice it.
        // Only a real (> 0) value: a 0 means the vehicle was priced without a gratuity, and
        // echoing it would pin every later quote untipped.
        const respTip = selVehicle?.pricing?.gratuity_percentage ?? selVehicle?.gratuity_percentage
        if (respTip) { setDriverTip(String(respTip)); setCommittedDriverTip(String(respTip)) }
        // Rebuild the trip's selected_* lists from live selections + type metadata so the
        // summary cards (which read the cache) reflect the pending change without a save.
        const selected_passengers = passengerTypes.filter(t => (passengers[t.id] || 0) > 0).map(t => ({...t, passenger_type_id: t.id, quantity: passengers[t.id]}))
        const selected_child_seats = childSeatTypes.filter(t => (childSeats[t.id] || 0) > 0).map(t => ({...t, child_seat_type_id: t.id, quantity: childSeats[t.id]}))
        const selected_luggage = [
            ...luggageTypes.filter(t => t.category === 'Standard' && (luggage[t.id] || 0) > 0).map(t => ({...t, luggage_type_id: t.id, quantity: luggage[t.id]})),
            ...luggageTypes.filter(t => t.category === 'Oversized' && (oversizedLuggage[t.id] || 0) > 0).map(t => ({...t, luggage_type_id: t.id, quantity: oversizedLuggage[t.id]})),
        ]
        // Store the preview as a local overlay (NOT the ['trip', rideId] cache, which the
        // 2s poll owns). `trip` above merges these fields over the live server trip.
        setPreviewOverlay({
            fields: {
                ...(selVehicle ? {selected_vehicle: selVehicle} : {}),
                selected_passengers, selected_child_seats, selected_luggage,
                origin: {...destinations.origin}, destination: {...destinations.destination},
                stop_overs: destinations.stopOvers.map(s => ({location: {address: s.address, lat: s.lat, lng: s.lng, city: s.city || '', state: s.state || '', country: s.country || ''}})),
                trip_direction: dir,
            },
            vehicleTypes: mapped.length ? mapped : null,
        })
    }, [selectedCar, passengerTypes, childSeatTypes, luggageTypes, passengers, childSeats, luggage, oversizedLuggage, destinations])

    // Route metadata from calculate-route, kept because a Draft response carries none of it:
    //   - routeDistanceMeters feeds /calculate-policies (the bands are distance-based)
    //   - routeData is the {distance_km, duration_minutes, legs} block RouteCard renders as
    //     `trip.route`, so the distance/time pills don't sit at "--"
    // Captured from EVERY route call this hook makes, so a route edit refreshes both.
    const [routeDistanceMeters, setRouteDistanceMeters] = useState(null)
    const [routeData, setRouteData] = useState(null)

    // The last route we measured + the stops it was measured FOR. A route_hash depends only on
    // the stops, so anything that re-prices WITHOUT moving them (the load quote right after the
    // measure pass, a date/time change, a tip, a coupon) can reuse this hash instead of paying
    // for a second /calculate-route. Change the stops and the key stops matching, so a
    // destination edit still measures fresh. An expired hash is still covered: calculate-cost
    // answers with a route-hash error and the retry re-measures.
    const measuredRouteRef = useRef({key: null, hash: null})
    const routeKeyOf = (d) => JSON.stringify([
        d?.origin?.lat, d?.origin?.lng,
        d?.destination?.lat, d?.destination?.lng,
        (d?.stopOvers || []).map((s) => [s?.lat, s?.lng]),
    ])

    const captureRouteMeta = useCallback((routeResult, forDestinations) => {
        const rd = routeResult?.data?.route_data ?? routeResult?.data?.routeData ?? null
        if (rd) setRouteData(rd)
        const hash = routeResult?.data?.route_hash ?? routeResult?.data?.routeHash ?? null
        if (hash) measuredRouteRef.current = {key: routeKeyOf(forDestinations), hash}
        // Always resolve to a number: null means "not measured yet" to the policies query, and
        // it must not stay stuck there when the response has no usable distance.
        const km = parseFloat(rd?.distance_km)
        setRouteDistanceMeters(km > 0 ? Math.round(km * 1000) : 0)
    }, [])

    // Refresh the route FIRST (so calculate-cost has a valid route_hash — user's precaution),
    // then calculate-cost. Returns {success, error?}. `tip`/`coupon` override current state.
    // The pickup time THIS PAGE has chosen — the picker's live value, else the trip's
    // saved one. /calculate-route is quoted against it so the duration reflects predicted
    // traffic for that window, and because the route_hash is bucketed by the window, a
    // time change here produces a fresh hash for calculate-cost and the submit.
    // Deliberately NOT the booking flow's sessionStorage time: that belongs to a
    // different session and would quote this trip against someone else's pickup.
    const livePickupAt = useCallback(
        () => (liveDatetime?.time ? liveDatetime.time.replace('T', ' ') : (trip?.scheduled_at || null)),
        [liveDatetime, trip?.scheduled_at],
    )

    const runPreviewRecalc = useCallback(async ({tip, coupon} = {}) => {
        const {origin, destination, stopOvers} = destinations
        const dir = directionFor(origin, destination)
        const passArr = toPassengerArr(passengers)
        const seatArr = toChildSeatArr(childSeats)
        const lugArr = toLuggageArr(luggage, oversizedLuggage)
        const effTip = tip != null ? tip : driverTip
        const effCoupon = (coupon !== undefined ? coupon : couponCode) || null
        // `force` skips the reuse — the stale-hash retry passes it so a second attempt always
        // measures again instead of handing back the hash that just failed.
        const getHash = async ({force = false} = {}) => {
            const cached = measuredRouteRef.current
            if (!force && cached.hash && cached.key === routeKeyOf(destinations)) return {hash: cached.hash}
            const r = await tripService.calculateRoute(origin, destination, stopOvers, {scheduledPickupAt: livePickupAt()})
            // Keep the distance + duration too: /calculate-policies is distance-banded, and
            // RouteCard's pills read this, so an edit that changes the route has to refresh
            // both alongside the re-price.
            if (r.success) captureRouteMeta(r, destinations)
            return r.success ? {hash: r.data?.route_hash ?? r.data?.routeHash} : {error: r.error || 'Could not calculate the route.'}
        }
        // Quote this DRAFT the way /confirm will price it. trip_id makes the backend read the
        // trip type off the draft (Initial and Return have separate discount percentages —
        // without it calculate-cost assumes Initial and a return booking quotes 20% then
        // confirms at 25%), and scheduled_at is what lets it see time-windowed surge. Same
        // value the confirm payload sends, so the quote and the booking can't disagree.
        const quoteOpts = {
            tripId: rideId,
            scheduledAt: liveDatetime?.time ? liveDatetime.time.replace('T', ' ') : (trip?.scheduled_at || null),
        }
        const first = await getHash()
        if (first.error) return {success: false, error: first.error}
        let cost = await tripService.calculateCost(first.hash, passArr, seatArr, lugArr, effCoupon, effTip, dir, quoteOpts)
        if (!cost.success && isRouteHashError(cost)) {
            const retry = await getHash({force: true})
            if (!retry.error) cost = await tripService.calculateCost(retry.hash, passArr, seatArr, lugArr, effCoupon, effTip, dir, quoteOpts)
        }
        if (!cost.success) return {success: false, error: cost.error || 'Could not calculate the price.'}
        applyPreview(cost.data || [], dir)
        return {success: true}
    }, [destinations, passengers, childSeats, luggage, oversizedLuggage, driverTip, couponCode, applyPreview, rideId, liveDatetime, trip?.scheduled_at])

    // Pair the car type coming off the trip with the full selectable so the rest of the hook
    // has a real `selectedCar` even when the backend sent no selected_vehicle (Draft). Both
    // land in the same hydration pass, so this runs on the following render.
    useEffect(() => {
        if (selectedCar || !availableCars.length) return
        const match = committedCarTypeId == null
            ? null
            : availableCars.find(c => String(c.vehicle_type_id ?? c.id) === String(committedCarTypeId))
        // No committed type (a Draft that was never priced), or one that is no longer
        // offered → take the first option rather than leaving the summary empty. Same
        // fallback applyPreview uses after a re-quote.
        const pick = match || availableCars[0]
        if (!pick) return
        setSelectedCar(pick)
        if (committedCarTypeId == null) setCommittedCarTypeId(pick.vehicle_type_id ?? pick.id)
    }, [selectedCar, committedCarTypeId, availableCars])

    // A location the user never touched came from GET /trips/{id}, which returns no
    // postal_code — so `postal_code: ''` would still fail the backend's "required" check.
    // Backfill from the coordinates with the same reverse-geocode PlaceAutocomplete uses
    // when a city-level place has no postal code of its own. Locations the user DID pick
    // already carry one and short-circuit.
    // (Declared above handleSectionDone because that callback lists it as a dependency —
    // a useCallback dep is read at RENDER time, so a later `const` would be a TDZ error.)
    const ensurePostalCodes = useCallback(async (d) => {
        const fill = async (loc) => {
            if (!loc || loc.postal_code || !loc.lat || !loc.lng) return loc
            const pc = await reverseGeocodePostalCode(parseFloat(loc.lat), parseFloat(loc.lng))
            return pc ? {...loc, postal_code: pc} : loc
        }
        const [origin, destination, stopOvers] = await Promise.all([
            fill(d.origin),
            fill(d.destination),
            Promise.all((d.stopOvers || []).map(fill)),
        ])
        return {origin, destination, stopOvers}
    }, [])

    // ── Write-through to the server (CONFIRM PAGES ONLY) ──
    //
    // The confirm pages no longer hold the passenger's edits in memory until Reserve Ride:
    // every section DONE, every tip / coupon / special-request save and every date+time pick
    // is written straight onto the Draft with POST /trips/{id}. The draft on the server is
    // therefore always current — which is the point on `/phone-booking-confirmation`, where
    // an agent may be looking at the same booking while the passenger edits it.
    //
    // The edit page is UNTOUCHED: it has its own hook (useEditTrip) and its own single-submit
    // rule, where each save costs the passenger one of their free modifications.
    //
    // NON-BLOCKING BY DESIGN. A failed write is reported (see `bookingError`) but never
    // reverts the local edit or blocks the flow, because the confirm payload still carries
    // every field — so the booking comes out right whether or not the write landed. That
    // matters while it is still unproven that this endpoint accepts a Draft at all: the
    // documented edit path is gated on `edit_policy.editable_fields`, which a Draft does not
    // have. If these writes turn out to 422, the page still works exactly as it did before
    // and the message says why.
    const persistTripFields = useCallback(async (fields) => {
        if (!fields || !Object.keys(fields).length) return {success: true}
        // DRAFTS ONLY. RideConfirm also runs this hook for a trip that is already live (its
        // "Edit Booking" mode), and there every POST /trips/{id} spends one of the
        // passenger's free modifications — write-through would burn the whole allowance on
        // a few DONEs. A live trip keeps the single-submit rule it has always had.
        if (trip?.status !== 'Draft') return {success: true}
        const result = await tripService.updateTrip(rideId, fields)
        if (result.success) {
            // Let the live poll re-read it rather than seating the response: this request is
            // sent without `without_polyline`, but writing a trimmed trip into the cache is
            // exactly how RouteCard's map would lose its polyline mid-edit.
            queryClient.invalidateQueries({queryKey: ['trip', rideId]})
            return {success: true}
        }
        // 409 = the server copy moved on under us (the 2s poll racing a write). Resync; the
        // user's own change is still in `previewOverlay`, so nothing is lost on screen.
        if (result?.statusCode === 409 || result?.raw?.code === 409) {
            queryClient.invalidateQueries({queryKey: ['trip', rideId]})
            return {success: true}
        }
        return {success: false, error: result.error || 'Your change was priced but could not be saved to the booking.'}
    }, [trip?.status, rideId, queryClient])

    // The fields ONE section owns, read from LIVE state — this runs before the section is
    // committed, so `committedX` is still the pre-edit baseline here.
    //
    // `trip_direction` is deliberately NOT sent with a route change. The backend guards it
    // ("switching a trip to from-airport needs a flight number to exist afterwards"), and on
    // this page the route is routinely set BEFORE the flight — sending it would 422 the one
    // edit most likely to flip the direction. The addresses are what the server needs; the
    // direction rides on the confirm payload, by which point the flight exists.
    const sectionUpdateFields = useCallback((section, dests) => {
        switch (section) {
            case 'destination': {
                const d = dests || destinations
                return {
                    origin: toLocationPayload(d.origin),
                    destination: toLocationPayload(d.destination),
                    stop_overs: (d.stopOvers || []).filter(s => s.address && s.lat && s.lng).map(toLocationPayload),
                }
            }
            case 'passengers': {
                const passenger_types = toPassengerArr(passengers)
                return {
                    passenger_types,
                    passenger_count: passenger_types.reduce((s, x) => s + x.quantity, 0),
                    child_seats: toChildSeatArr(childSeats),
                }
            }
            case 'luggage':
                return {luggage: toLuggageArr(luggage, oversizedLuggage)}
            case 'car':
                return {
                    vehicle_type_id: selectedCar?.vehicle_type_id ?? selectedCar?.id,
                    gratuity_percentage: driverTip ? parseInt(driverTip) : null,
                }
            default:
                return null
        }
    }, [destinations, passengers, childSeats, luggage, oversizedLuggage, selectedCar, driverTip])

    // ── Section DONE: re-price, WRITE TO THE SERVER, then commit locally ──
    const handleSectionDone = useCallback(async (sectionName) => {
        setIsRecalculating(true); setSectionSaveError(null)
        const res = await runPreviewRecalc()
        if (!res.success) { setSectionSaveError(res.error || 'Failed to update price.'); setIsRecalculating(false); return }
        // Persist this section. Locations get their postal codes backfilled first — the same
        // "required" check the confirm payload has to satisfy applies to an update.
        const dests = sectionName === 'destination' ? await ensurePostalCodes(destinations) : null
        const saved = await persistTripFields(sectionUpdateFields(sectionName, dests))
        setBookingError(saved.success ? null : saved.error)
        // Commit the just-edited section (baseline for dirty-check / RouteCard / revert / submit).
        if (sectionName === 'destination') {
            // If this edit flips the trip direction (e.g. toAirport ⇄ fromAirport), the
            // previously-entered flight no longer matches the new leg. Drop it so the
            // flight-required gate + airport-mismatch validation re-apply at runtime —
            // exactly like a reload. Date/time are intentionally preserved.
            const prevDir = directionFor(committedDestinations.origin, committedDestinations.destination)
            const nextDir = directionFor(destinations.origin, destinations.destination)
            setCommittedDestinations({...destinations})
            setFlightNumber('')
            if (prevDir !== nextDir) {
                setSharedFlightNumber('')
                setSharedFlightDetails(null)
                setSharedFlightError('')
                setSharedTimeApplied(false)
                setFlightHint(nextDir === TRIP_DIRECTIONS.FROM_AIRPORT)
            }
        }
        if (sectionName === 'passengers') { setCommittedPassengers({...passengers}); setCommittedChildSeats({...childSeats}) }
        if (sectionName === 'luggage') { setCommittedLuggage({...luggage}); setCommittedOversizedLuggage({...oversizedLuggage}) }
        if (sectionName === 'car') { setCommittedCarTypeId(selectedCar?.vehicle_type_id ?? selectedCar?.id); setCommittedDriverTip(driverTip) }
        setConfirmStep(0)
        setEditingSection(null)
        setIsRecalculating(false)
    }, [destinations, committedDestinations, passengers, childSeats, luggage, oversizedLuggage, selectedCar, driverTip, runPreviewRecalc, ensurePostalCodes, persistTripFields, sectionUpdateFields])

    // (There used to be a `structuralUpdateFields` builder here, assembling the whole
    // create payload from committed state for the submit. The section write-throughs put
    // every one of those fields on the draft as it changes, so the confirm payload no
    // longer needs it — see buildPayload below. `sectionUpdateFields` is what builds the
    // per-section update bodies now.)

    // ── Confirm (Draft → live booking — used by both confirm pages' main submit) ──
    // ONE request, carrying only what the write-throughs could not already have saved —
    // see buildPayload. The route travels as route_hash (like create), not as
    // origin/destination.
    const doConfirm = useCallback(async () => {
        // A schedule-less draft must not confirm without an explicit date & time pick
        // (liveDatetime.time stays null until both parts are chosen — see DateTimeCard).
        // scheduled_at is REQUIRED by /confirm — there is no ASAP draft.
        if (!liveDatetime?.time && !trip?.scheduled_at) {
            setBookingError('Please select the arrival date and time before confirming.')
            return
        }
        // vehicle_type_id is REQUIRED by /confirm. Caught here rather than letting the
        // request 422, since a Draft resolves its car indirectly (no selected_vehicle).
        if ((selectedCar?.vehicle_type_id ?? selectedCar?.id) == null) {
            setBookingError('Please choose a vehicle before confirming.')
            return
        }
        setConfirmStep(2)
        setIsSubmitting(true); setBookingError(null)
        const effectiveFlightNumber = flightNumber || sharedFlightNumber || null
        try {
            // `dir` below comes from isNearAirport, which reads the airport list out of the
            // react-query cache synchronously. It is fetched at app root, so this resolves
            // from cache — it only actually waits if the user confirmed faster than the
            // request, which would otherwise send an airport trip as 'others'.
            await ensureAirports()
            // Backfill any postal_code the trip GET didn't return before pricing the route.
            const dests = await ensurePostalCodes(committedDestinations)
            // Direction from the destinations actually being sent, NOT the live-state
            // `tripDirection` — those can disagree while a section edit is open.
            const dir = directionFor(dests.origin, dests.destination)

            // A from-airport leg can't be confirmed without a flight number — the API 422s.
            // Return drafts are where this bites: a return born from a to-airport booking IS
            // from-airport, and /confirm resolves the direction from the Draft when the
            // payload omits it, so the leg needs a flight even though the original didn't.
            if (dir === TRIP_DIRECTIONS.FROM_AIRPORT && !effectiveFlightNumber) {
                setBookingError('Please add your flight number before confirming — we track the arrival so the driver meets the flight you land on.')
                return   // finally clears isSubmitting
            }

            const freshHash = async () => {
                const r = await tripService.calculateRoute(dests.origin, dests.destination, dests.stopOvers, {scheduledPickupAt: livePickupAt()})
                return r.success ? {hash: r.data?.route_hash ?? r.data?.routeHash} : {error: r.error || 'Could not calculate the route.'}
            }
            const first = await freshHash()
            if (first.error) { setBookingError(first.error); return }   // finally clears isSubmitting

            // The airport (and terminal) this leg touches, matched from the SAME locations
            // `dir` was decided from. Spread in only when there is an airport — a
            // city-to-city leg sends neither key rather than two nulls.
            const airportIds = getAirportIdsFor(dests.origin, dests.destination)

            /*
             * SLIM PAYLOAD — the booking DETAILS are not re-sent.
             *
             * Every detail the passenger can change is written onto the draft the moment
             * they change it (see persistTripFields), so passenger_types, child_seats,
             * luggage, discount_code, gratuity_percentage, special_request, having_pet and
             * quiet_ride are already on the server and are deliberately omitted here.
             *
             * What stays, and why each one has to:
             *   route_hash          required by /confirm, and not a field an update can carry
             *   vehicle_type_id     required by /confirm
             *   scheduled_at        required by /confirm
             *   payment_method_id   /confirm needs a payment source and validates it here
             *   airport_id / _terminal_id
             *                       confirm-only — UpdateTripRequest has no airport keys, so
             *                       no write-through could ever have carried them
             *   trip_direction      NOT written through: the destination write deliberately
             *                       omits it (the backend requires a flight to exist when a
             *                       trip switches to from-airport, and here the route is
             *                       routinely set before the flight), so the draft's copy
             *                       can be stale after a route change that flips direction
             *   flight_number       written through, but DEBOUNCED — a number typed and
             *                       confirmed inside the debounce window would not have
             *                       landed, and /confirm 422s a from-airport leg without one
             *
             * ⚠️ `tripService.confirmTrip` documents this endpoint as overwriting the Draft
             * WHOLESALE (it runs the create pipeline, where an absent list means an empty
             * list). If that holds, omitting the detail fields CLEARS them. Confirmed as the
             * intended behaviour by the user on 2026-08-21 on the grounds that the update
             * requests already carry them — so the first thing to check on a real confirm is
             * that the booking-received screen still shows the right passengers and luggage.
             */
            const buildPayload = (routeHash) => ({
                route_hash: routeHash,
                vehicle_type_id: selectedCar?.vehicle_type_id ?? selectedCar?.id,
                scheduled_at: liveDatetime?.time ? liveDatetime.time.replace('T', ' ') : trip?.scheduled_at,
                payment_method_id: selectedCardId ?? null,
                ...(airportIds.airport_id ? {airport_id: airportIds.airport_id} : {}),
                ...(airportIds.airport_terminal_id ? {airport_terminal_id: airportIds.airport_terminal_id} : {}),
                trip_direction: dir,
                flight_number: effectiveFlightNumber,
            })

            let confirmResult = await tripService.confirmTrip(rideId, buildPayload(first.hash))
            // The hash can expire between calculate-cost and Confirm — one silent re-run.
            if (isRouteHashError(confirmResult)) {
                const retry = await freshHash()
                if (!retry.error) confirmResult = await tripService.confirmTrip(rideId, buildPayload(retry.hash))
            }

            if (confirmResult.success) {
                Object.keys(sessionStorage).forEach(key => { if (key.startsWith(`${EDIT_CACHE_PREFIX}${rideId}-`)) sessionStorage.removeItem(key) })
                if (effectiveFlightNumber && !flightNumber) setFlightNumber(effectiveFlightNumber)
                // The response IS the fully priced trip — seat it so the success screen
                // doesn't race the refetch for final_price / pricing_breakdown.
                const confirmed = confirmResult.data?.trip || confirmResult.data
                if (confirmed && typeof confirmed === 'object') {
                    queryClient.setQueryData(['trip', rideId], (prev) => prev ? {...prev, trip: confirmed} : prev)
                }
                queryClient.invalidateQueries({queryKey: ['trips']}); queryClient.invalidateQueries({queryKey: ['trip', rideId]})
                setPreviewOverlay(null) // saved — the refetch shows it from the server now
                setShowSuccess(true)
            } else if (isTripNotDraftError(confirmResult)) {
                // Not a failure: the booking already went through (double submit / retry of
                // a call that actually landed). Confirm is one-shot, so never retry — just
                // resync and show the same success screen the first call would have.
                queryClient.invalidateQueries({queryKey: ['trips']}); queryClient.invalidateQueries({queryKey: ['trip', rideId]})
                setPreviewOverlay(null)
                setShowSuccess(true)
            } else if (confirmResult?.statusCode === 409 || confirmResult?.raw?.code === 409) {
                queryClient.invalidateQueries({queryKey: ['trip', rideId]}); queryClient.invalidateQueries({queryKey: ['trips']})
            } else if (isPaymentMethodUnusableError(confirmResult)) {
                // The card was verified at submit and refused (expired, deactivated, or from
                // the other Square environment). Drop the selection and refetch the list —
                // the listing filters unusable cards out, so the refetch is what removes the
                // bad one from the card section instead of leaving it there to fail again.
                setSelectedCardId(null)
                queryClient.invalidateQueries({queryKey: ['payment-methods']})
                setBookingError(confirmResult.raw?.message || PAYMENT_METHOD_UNUSABLE_MESSAGE)
            } else if (isIdentityIncompleteError(confirmResult)) {
                // The account owes the gate a field — in practice the phone.
                //
                // ⚠️ THIS IS THE FLOW THAT WILL HIT IT MOST. A trip created at the phone desk
                // belongs to a placeholder identity until the passenger claims it, so this
                // handoff confirmation is exactly where a real name/email/phone is first
                // demanded. Surfaced as the notice (which carries the route to the profile
                // page) rather than as error text, because the Draft survives a failed
                // confirm — the passenger can fill the field in and come straight back.
                setIdentityMissing(identityMissingFromError(confirmResult))
            } else {
                // A failed confirm leaves the Draft untouched — safe to fix and retry.
                setBookingError(confirmErrorMessage(confirmResult))
            }
        } catch {
            setBookingError('Something went wrong. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }, [flightNumber, sharedFlightNumber, selectedCardId, selectedCar, rideId, queryClient, liveDatetime, trip, committedDestinations, ensurePostalCodes])

    // ── Draft-only wiring: route, policies, price ──

    // NO price gate here any more. The fare is quoted on load (see step 3) and shown as soon as
    // calculate-cost answers, so there is no "Add date/time to see the price" veil, no mocked
    // card and no dummy breakdown. While a quote is in flight CarDetailsCard shows its own
    // loader off `isRecalculating`, which is the honest state — an empty card, not a fake one.

    // The route block RouteCard renders (pills + per-leg times) and DateTimeSection reads for
    // smart-time travel minutes: the /calculate-route RESPONSE and nothing else. `route_data`
    // already matches the shape those components expect ({distance_km, duration_minutes,
    // distance_text, duration_text, legs, polyline}).
    const route = routeData ?? null

    // Base for the clock times RouteCard shows ("1:38 AM" above each Travel time). It walks the
    // legs forward from this, so a base is REQUIRED — /calculate-route carries durations only,
    // no timestamps. Order: the passenger's pick → NOW, so the rows show a time before a date is
    // chosen and re-base to the real one the moment it exists. tripNow() keeps it on
    // APP_TIMEZONE wall-clock; RouteCard takes it directly (it only does new Date(value)).
    const scheduledAt = liveDatetime?.time || trip?.scheduled_at || tripNow()

    // Locations RENDERED here come from the route RESPONSE — the trip's are only the INPUT to
    // that call. Intermediate stops are leg boundaries: every leg's end_location EXCEPT the
    // last. Falls back to committed state until the first measurement lands, so nothing blanks.
    const routeLocations = useMemo(() => {
        const legs = route?.legs || []
        const stops = legs.length > 1
            ? legs.slice(0, -1).map((l) => ({location: l.end_location}))
            : (route?.stop_overs || []).map((s) => ({location: s}))
        return {
            origin: route?.start_location ?? committedDestinations.origin,
            destination: route?.end_location ?? committedDestinations.destination,
            stop_overs: route ? stops : committedDestinations.stopOvers,
        }
    }, [route, committedDestinations])

    // 1. Measure the route on every load — a Draft has no route block at all. This is the ONLY
    //    /calculate-route on load: captureRouteMeta stores the hash against these stops, so the
    //    price pass below reuses it instead of measuring the same route a second time.
    const measuredRef = useRef(null)
    useEffect(() => {
        if (!trip || measuredRef.current === rideId) return
        if (!destinations.origin?.lat || !destinations.destination?.lat) return
        measuredRef.current = rideId
        tripService.calculateRoute(destinations.origin, destinations.destination, destinations.stopOvers, {scheduledPickupAt: livePickupAt()})
            .then((r) => {
                // captureRouteMeta resolves the distance to 0 on an unusable response, which
                // still yields the config policy bands rather than blocking them forever.
                if (r.success) captureRouteMeta(r, destinations)
                else setRouteDistanceMeters(0)
            })
    }, [trip, rideId, destinations, captureRouteMeta])

    // 2. Policies on every page load — this is what makes Popular Questions and Exclusive
    //    Airport Pickup Perks show live values instead of curated fallbacks. Same hook the
    //    booking modal uses; only the params differ (trip instead of sessionStorage). It writes
    //    the shared cache key, which is what makes POLICY_CONSTANTS, bookingPolicyValues() and a
    //    prop-less CMR popup resolve here unchanged. Waits for the distance from step 1 so this
    //    is ONE call carrying the real value rather than a 0-distance call plus a re-band.
    usePolicies({
        tripDirection,
        distanceMeters: routeDistanceMeters ?? 0,
        enabled: routeDistanceMeters != null,
    })

    // 3. Price on EVERY load — a Draft carries no fare of its own, so calculate-route (step 1)
    //    is followed straight away by calculate-cost and the card renders that. It runs again
    //    whenever the date/time CHANGES, because surge is time-windowed: the first quote (no
    //    pickup time yet) is a base fare, and re-quoting once a time exists is what surfaces
    //    the surge. Keyed by rideId + the chosen datetime — 'none' while there isn't one — so
    //    the load quote and each later time change fire exactly once and the 2s poll is ignored.
    const pricedForRef = useRef(null)
    useEffect(() => {
        if (!trip) return
        if (!destinations.origin?.lat || !destinations.destination?.lat) return
        // WAIT for step 1 to land. Both effects run in the same commit, so pricing straight away
        // would find no measured hash to reuse and would measure the very same route a second
        // time — two /calculate-route calls on every load. routeDistanceMeters is set by
        // captureRouteMeta on success AND forced to 0 on failure, so this resolves either way
        // and can't deadlock the fare behind a bad route response.
        if (routeDistanceMeters == null) return
        // liveDatetime.time stays null until BOTH parts are chosen (see DateTimeCard).
        const when = liveDatetime?.time || trip.scheduled_at
        const key = `${rideId}|${when || 'none'}`
        if (pricedForRef.current === key) return
        pricedForRef.current = key
        // A pickup the USER just chose (as opposed to the load quote of what the draft
        // already carried) is written through as well — see persistTripFields. The whole
        // span, re-quote included, runs under `isSavingDateTime`: the price is what makes a
        // pickup time meaningful, so the field stays locked until BOTH have settled rather
        // than re-opening over a fare that is still moving.
        const pickedAt = liveDatetime?.time ? liveDatetime.time.replace('T', ' ') : null
        const isUserPick = !!pickedAt && pickedAt !== trip.scheduled_at
        setIsRecalculating(true)
        if (isUserPick) { setIsSavingDateTime(true); setBookingError(null) }
        runPreviewRecalc()
            .then(async (res) => {
                if (!res?.success) { setSectionSaveError(res?.error || 'Could not calculate the price.'); return }
                if (!isUserPick) return
                const saved = await persistTripFields({scheduled_at: pickedAt})
                if (!saved.success) setBookingError(saved.error)
            })
            .finally(() => { setIsRecalculating(false); setIsSavingDateTime(false) })
    }, [trip, rideId, destinations, liveDatetime, runPreviewRecalc, routeDistanceMeters, persistTripFields])

    // The flight the SERVER holds, as a primitive — see the effect's dep note below.
    const tripFlightNumber = trip?.flight_number || ''

    /*
     * 4. Flight number — written through on the two moments the passenger ADDS one, and
     *    never while they are typing.
     *
     * ⚠️ DO NOT go back to writing on the input (debounced or otherwise). It was tried, and
     * it silently ADDED the flight for them: `hasFlightNumber` counts a trip whose saved
     * `flight_number` equals what is in the field, so the moment a half-typed "AC78" was
     * written to the draft the pill flipped from Add to ✕ and the submit gate opened — with
     * no lookup ever having run. Pressing Add is the passenger's decision to make.
     *
     * The two write points:
     *   • `handleFlightAdd` below — the Add button, which also opens Smart Time.
     *   • this effect — a flight the LOOKUP confirmed, which covers a number typed (or
     *     changed) inside the Smart Time modal, where there is no Add button to press.
     *
     * `flightSavedRef` dedupes so the two can't double-post the same number, and CLEARING
     * goes through `clearFlight` below.
     */
    const flightSavedRef = useRef(null)
    const persistFlightNumber = useCallback(async (num) => {
        const eff = (num || '').trim()
        if (!eff || eff === tripFlightNumber || flightSavedRef.current === eff) return
        flightSavedRef.current = eff
        const saved = await persistTripFields({flight_number: eff})
        if (!saved.success) setBookingError(saved.error)
    }, [tripFlightNumber, persistTripFields])

    // The Add button: save what the passenger typed, then open Smart Time. Both confirm
    // pages pass this as DateTimeCard's `onSmartTime`; the EDIT page keeps its own plain
    // `setSmartTimePopup(true)` (it persists on its single submit, not per field).
    const handleFlightAdd = useCallback(() => {
        persistFlightNumber(sharedFlightNumber)
        setSmartTimePopup(true)
    }, [persistFlightNumber, sharedFlightNumber])

    useEffect(() => {
        if (!sharedFlightDetails) return
        persistFlightNumber(sharedFlightNumber)
    }, [sharedFlightDetails, sharedFlightNumber, persistFlightNumber])

    /*
     * The ✕ on the flight pill: wipe the field AND tell the server, so adding and removing
     * a flight both mirror onto the booking straight away.
     *
     * This deliberately posts even on a from-airport trip, where the backend may refuse
     * ("may not clear it on a from-airport trip"). An earlier cut skipped the write for
     * that reason and let the confirm payload settle it, which left the desk looking at a
     * flight the passenger had already removed. If the server does refuse, its own message
     * lands in `bookingError` — and "this ride needs a flight number" is exactly what a
     * passenger clearing the flight on a from-airport leg should be told.
     *
     * `flightSavedRef` is reset so re-typing the SAME number afterwards writes again rather
     * than being deduped against the value that is no longer on the trip.
     */
    const clearFlight = useCallback(async () => {
        handleFlightNumberChange('')
        flightSavedRef.current = null
        const saved = await persistTripFields({flight_number: null})
        setBookingError(saved.success ? null : saved.error)
    }, [handleFlightNumberChange, persistTripFields])

    // ── Finalize flight number (isToAirport optional capture) ──
    const finalizeFlightMutation = useMutation({
        mutationFn: () => tripService.updateTrip(rideId, {flight_number: sharedFlightNumber, without_polyline: true, trip_direction: tripDirection}),
        onSuccess: (result) => {
            if (result.success) {
                setFlightNumber(sharedFlightNumber)
                setShowFlightPrompt(false)
                setShowSuccess(true)
                setFlightFinalizeError(null)
                queryClient.invalidateQueries({queryKey: ['trip', rideId]})
            } else if (result?.statusCode === 409 || result?.raw?.code === 409) {
                queryClient.invalidateQueries({queryKey: ['trip', rideId]}); queryClient.invalidateQueries({queryKey: ['trips']})
                setShowFlightPrompt(false)
            } else {
                setFlightFinalizeError(result.error || 'Could not save flight number. Please try again.')
            }
        },
        onError: () => setFlightFinalizeError('Something went wrong. Please try again.'),
    })

    // ── Cancel ──
    const cancelMutation = useMutation({
        mutationFn: (reason) => tripService.cancelTrip(rideId, reason),
        onSuccess: (result) => {
            // 409 = trip already changed (race / already cancelled) — treat as stale, not an error.
            const stale = result?.statusCode === 409 || result?.raw?.code === 409
            // DON'T navigate here — the cancel popup shows its own "Ride Cancelled"
            // success screen and the user leaves from there (Book New Ride / ✕).
            // We only refetch so the trip reflects the cancelled state + receipt link.
            if (result.success || stale) { queryClient.invalidateQueries({queryKey: ['trips']}); queryClient.invalidateQueries({queryKey: ['trip', rideId]}) }
        },
    })

    // ── Change handlers ──
    const handleDestinationsChange = useCallback((v) => { setDestinations(typeof v === 'function' ? v : {...v}) }, [])
    const handlePassengersChange = useCallback((v) => { setPassengers(typeof v === 'function' ? v : {...v}) }, [])
    const handleChildSeatsChange = useCallback((v) => { setChildSeats(typeof v === 'function' ? v : {...v}) }, [])
    const handleLuggageChange = useCallback((v) => { setLuggage(typeof v === 'function' ? v : {...v}) }, [])
    const handleOversizedChange = useCallback((v) => { setOversizedLuggage(typeof v === 'function' ? v : {...v}) }, [])
    const toggle = useCallback((s) => setExpanded(p => { const n = new Set(p); n.has(s) ? n.delete(s) : n.add(s); return n }), [])

    // Tip change → re-price, then write through (see persistTripFields).
    const handleTipChange = useCallback(async (tipVal) => {
        setCommittedDriverTip(String(tipVal))
        const res = await runPreviewRecalc({tip: tipVal})
        if (res.success) {
            const saved = await persistTripFields({gratuity_percentage: tipVal ? parseInt(tipVal) : null})
            setBookingError(saved.success ? null : saved.error)
        }
        return res
    }, [runPreviewRecalc, persistTripFields])

    // Coupon apply → re-price, then write through.
    const handleCouponRecalculate = useCallback(async (code) => {
        setCouponCode(code || '')
        const res = await runPreviewRecalc({coupon: code || null})
        if (res.success) {
            const saved = await persistTripFields({discount_code: code || ''})
            setBookingError(saved.success ? null : saved.error)
        }
        return res
    }, [runPreviewRecalc, persistTripFields])

    // Card picked (or a new card added, which selects it) → write through. Nothing to
    // re-price: the card doesn't move the fare, it just says who pays. Both confirm pages
    // hand this to PaymentMethodsCard as its `setSelectedCardId`, so every row tap is a
    // save — the desk's Booking Details page reads `selected_payment_method` off the trip
    // and had no way to see the passenger's choice before they confirmed.
    const handleCardSelect = useCallback(async (cardId) => {
        setSelectedCardId(cardId)
        const saved = await persistTripFields({payment_method_id: cardId ?? null})
        setBookingError(saved.success ? null : saved.error)
    }, [persistTripFields])

    // Special request DONE → nothing to re-price (notes / pet / quiet ride don't move the
    // fare), so it writes through on its own. Both confirm pages call this instead of
    // setting state inline, so the popup's Save is a save.
    const handleSpecialRequestSave = useCallback(async (val) => {
        setSpecialRequest(val)
        setShowSRPopup(false)
        const saved = await persistTripFields({
            // Capped at the SUBMISSION boundary — a trip created before this cap existed
            // carries an over-length note, and confirming without opening that field would
            // send it straight back. See BOOKING_CONSTRAINTS.
            special_request: (val?.notes || '').slice(0, BOOKING_CONSTRAINTS.SPECIAL_REQUEST_MAX_LENGTH),
            having_pet: val?.caringPet === true,
            quiet_ride: val?.quietRide === true,
        })
        setBookingError(saved.success ? null : saved.error)
    }, [persistTripFields])

    return {
        // Data
        trip, isLoading, rideId, getSectionPolicy, getFieldPolicy, getCancelPolicy,
        // Draft-only: see the block above.
        route, routeLocations, scheduledAt,
        // ── Seam for useConfirmTrip ──
        // The /confirm page composes this hook and layers its own Draft behaviour on top
        // (own pricing pass, own policy source). These are the handles it needs; the edit
        // page ignores them.
        previewOverlay, runPreviewRecalc, setIsRecalculating, setSectionSaveError,
        routeDistanceMeters, setRouteDistanceMeters, routeData, captureRouteMeta,
        // Live state
        destinations, passengers, childSeats, luggage, oversizedLuggage,
        // Committed
        committedDestinations, committedPassengers, committedChildSeats, committedLuggage, committedOversizedLuggage,
        // Vehicle
        selectedCar, setSelectedCar, committedCarTypeId, availableCars,
        // Other
        selectedCardId, setSelectedCardId, savedCards, setSavedCards, specialRequest, setSpecialRequest,
        contact,
        driverTip, setDriverTip, flightNumber, couponCode,
        // Selectables
        passengerTypes, childSeatTypes, luggageTypes,
        // UI
        editingSection, passengerView, setPassengerView, passengerOnly, destinationPart, luggageOversizedMode, setLuggageOversizedMode, carBreakdownId, setCarBreakdownId, isSubmitting, isRecalculating, isSavingDateTime, sectionSaveError, bookingError, identityMissing, setIdentityMissing, showSuccess,
        expanded, confirmStep, setConfirmStep, showCancelPopup, setShowCancelPopup, showSRPopup, setShowSRPopup, infoPopup, setInfoPopup,
        showFlightPrompt, setShowFlightPrompt, flightFinalizeError, finalizeFlightMutation,
        // DateTime
        liveDatetime, setLiveDatetime, minLeadTimeError,
        // Flight
        sharedFlightNumber, setSharedFlightNumber, sharedFlightDetails, setSharedFlightDetails,
        sharedFlightLoading, setSharedFlightLoading, sharedFlightError, setSharedFlightError,
        sharedFlightUnavailable, setSharedFlightUnavailable,
        sharedTimeApplied, setSharedTimeApplied, hasFlightNumber, handleFlightNumberChange, clearFlight, handleFlightAdd,
        // Hints
        dateHint, setDateHint, flightHint, setFlightHint, smartTimePopup, setSmartTimePopup, closeSmartTime, setFlightDropOnClose,
        skipHintTransition, dateTimeSectionRef, applySmartTimeRef,
        // Airport
        isFromAirport, isToAirport, isAirportTrip, tripDirection,
        // Handlers
        handleDateChange, handleTimeChange, handleSmartTimeSelect, openEdit, closeEdit,
        handleSectionDone, doConfirm, cancelMutation, toggle,
        handleDestinationsChange, handlePassengersChange, handleChildSeatsChange, handleLuggageChange, handleOversizedChange,
        handleTipChange, handleCouponRecalculate, handleSpecialRequestSave, handleCardSelect,
    }
}
